import React, { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useUiFeedback } from "../../../context/ui-feedback/ui-feedback";
import { createErrorFeedback } from "../../../context/ui-feedback/action";

import { useSearchEmails } from "../../../api/email-api";
import { PageLayout } from "../../common/page-layout/page-layout";
import { ControllerRow } from "../../common/controller-row/controller-row";
import { Pager } from "../../common/pager/pager";
import { EmailSearch } from "./component/emil-search";
import { EmailTable } from "./component/email-table";

export const Emails = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const localSearchUUID = useRef(null);
  const [errorInLoading, setErrorInLoading] = useState(false);
  const [listQuery, setListQuery] = useState({
    query: {
      limit: 10,
      offset: 0,
      to: null,
      subject: null,
      status: null,
      date: null,
    },
    allCount: null,
    results: null,
  });

  const searchEmails = useSearchEmails();

  useEffect(() => {
    const loadEmails = async () => {
      setErrorInLoading(false);
      setListQuery({
        ...listQuery,
        results: null,
      });
      try {
        const search = {
          offset: listQuery.query.offset,
          limit: listQuery.query.limit,
          to: listQuery.query.to,
          subject: listQuery.query.subject,
          status: listQuery.query.status,
          date: listQuery.query.date,
        };
        await searchEmails(search, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            if (data.emails.length === 0 && data.allCount > 0) {
              setListQuery({
                ...listQuery,
                query: {
                  ...listQuery.query,
                  offset:
                    (Math.ceil(data.allCount / listQuery.limit) - 1) *
                    listQuery.limit,
                },
              });
              localSearchUUID.current = null;
            } else {
              setListQuery({
                ...listQuery,
                allCount: data.allCount,
                results: data.emails,
              });
            }
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          setErrorInLoading(true);
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.emails.exception.listemails.title",
            }),
            intl.formatMessage({
              id: "page.emails.exception.listemails.description",
            }),
          );
        }
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      localSearchUUID.current === null &&
      checkNotEmpy([
        intl,
        listQuery,
        navigate,
        searchEmails,
        uiFeedbackDispatch,
      ])
    ) {
      localSearchUUID.current = new Date().getTime();
      loadEmails();
    }
  }, [
    localSearchUUID,
    listQuery.query,
    intl,
    listQuery,
    navigate,
    searchEmails,
    uiFeedbackDispatch,
  ]);

  const changePage = (page) => {
    setListQuery({
      ...listQuery,
      query: {
        ...listQuery.query,
        offset: page * listQuery.query.limit,
      },
    });
    localSearchUUID.current = null;
  };

  const handleSearch = (searchParam) => {
    setListQuery({
      ...listQuery,
      query: {
        ...listQuery.query,
        limit: 10,
        offset: 0,
        to: searchParam.to,
        subject: searchParam.subject,
        status: searchParam.status,
        date: searchParam.date,
      },
    });
    localSearchUUID.current = null;
  };

  const buildPaths = () => {
    return [
      {
        link: "/",
        title: intl.formatMessage({ id: "page.welcome.title" }),
      },
    ];
  };

  return (
    <>
      <PageLayout
        paths={buildPaths()}
        title={intl.formatMessage({ id: "page.emails.title" })}
      >
        <>
          <ControllerRow>
            <EmailSearch
              to={listQuery.query.to}
              subject={listQuery.query.subject}
              status={listQuery.query.status}
              date={listQuery.query.date}
              handleSearch={handleSearch}
            />
          </ControllerRow>
          <Pager
            currentPage={listQuery.query.offset / listQuery.query.limit}
            allPage={Math.ceil(listQuery?.allCount / listQuery.query.limit)}
            topPager={true}
            bottomPager={true}
            handleChangePage={changePage}
          >
            <EmailTable
              rowNumberInit={listQuery.query.offset}
              emails={listQuery?.results}
              hasError={errorInLoading}
            />
          </Pager>
        </>
      </PageLayout>
    </>
  );
};
